import { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  Bot,
  Check,
  ChevronLeft,
  Command,
  Download,
  ExternalLink,
  Inbox,
  Languages,
  LoaderCircle,
  MessageCircleMore,
  Radio,
  RefreshCw,
  Scale,
  ShieldCheck,
  SendHorizontal,
  Smartphone,
  Sparkles,
  TriangleAlert,
  WalletCards,
} from "lucide-react";
import type { HermesFinancialReportV1, OpportunityDecisionRoom } from "@tradvera/contracts";
import { usePreview } from "@tradvera/design-system";
import { AccountControl, useAuth } from "../Auth";
import { bootstrapOwnerPush, companionRequest, decisionRoomRequest, hermesChatRequest, pushKeyBytes, type CompanionBootstrap, type CompanionNotification, type CompanionPortfolio, type HermesChatMessage } from "./api";
import { MobileDecisionRoom } from "./MobileDecisionRoom";
import { FinancialReportPocket } from "./FinancialReportPocket";
import { MobileSafeActions } from "./MobileSafeActions";

type TabId = "chat" | "inbox" | "portfolio" | "decisions" | "actions";
type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

const tabs: Array<{ id: TabId; ar: string; en: string; icon: typeof Inbox }> = [
  { id: "chat", ar: "هيرمس", en: "Hermes", icon: MessageCircleMore },
  { id: "inbox", ar: "الوارد", en: "Inbox", icon: Inbox },
  { id: "portfolio", ar: "المحفظة", en: "Portfolio", icon: WalletCards },
  { id: "decisions", ar: "القرار", en: "Decision", icon: Scale },
  { id: "actions", ar: "الأوامر", en: "Actions", icon: Command },
];

export function CompanionApp() {
  const { user, preview } = useAuth();
  const { preferences, setLocale } = usePreview();
  const ar = preferences.locale === "ar";
  const initialTab = new URLSearchParams(window.location.search).get("tab") as TabId | null;
  const [tab, setTab] = useState<TabId>(tabs.some((item) => item.id === initialTab) ? initialTab! : "chat");
  const [data, setData] = useState<CompanionBootstrap | null>(null);
  const [selected, setSelected] = useState<CompanionNotification | null>(null);
  const [busy, setBusy] = useState<"load" | "simulate" | "push" | "ack" | "chat" | null>(null);
  const [message, setMessage] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<HermesChatMessage[]>([]);
  const [decisionRoom, setDecisionRoom] = useState<OpportunityDecisionRoom | null>(null);
  const [decisionError, setDecisionError] = useState("");
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(null);
  const [installed, setInstalled] = useState(() => window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

  const t = (arabic: string, english: string) => ar ? arabic : english;
  const unreadCount = data?.unreadCount ?? 0;
  const newest = data?.notifications[0] ?? null;

  useEffect(() => {
    document.title = "Tradvera Companion — Learn. Decide. Trade";
    document.documentElement.dataset.surface = "companion";
    const manifest = document.createElement("link");
    manifest.rel = "manifest";
    manifest.href = "/companion.webmanifest";
    document.head.appendChild(manifest);
    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    apple.href = "/brand/tradvera-apple-touch.png";
    document.head.appendChild(apple);
    const mobileCapable = document.createElement("meta");
    mobileCapable.name = "apple-mobile-web-app-capable";
    mobileCapable.content = "yes";
    document.head.appendChild(mobileCapable);
    const mobileStatus = document.createElement("meta");
    mobileStatus.name = "apple-mobile-web-app-status-bar-style";
    mobileStatus.content = "black-translucent";
    document.head.appendChild(mobileStatus);
    const mobileTitle = document.createElement("meta");
    mobileTitle.name = "apple-mobile-web-app-title";
    mobileTitle.content = "Tradvera";
    document.head.appendChild(mobileTitle);
    return () => {
      manifest.remove();
      apple.remove();
      mobileCapable.remove();
      mobileStatus.remove();
      mobileTitle.remove();
      delete document.documentElement.dataset.surface;
    };
  }, []);

  useEffect(() => {
    const capture = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPrompt);
    };
    const confirm = () => { setInstalled(true); setInstallPrompt(null); };
    window.addEventListener("beforeinstallprompt", capture);
    window.addEventListener("appinstalled", confirm);
    return () => {
      window.removeEventListener("beforeinstallprompt", capture);
      window.removeEventListener("appinstalled", confirm);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    void navigator.serviceWorker.register("/companion-sw.js", { scope: "/companion/" });
  }, []);

  useEffect(() => {
    if (!user) return;
    void load();
  }, [user]);

  useEffect(() => {
    const notificationId = new URLSearchParams(window.location.search).get("notification");
    if (!user || !notificationId || !data) return;
    const match = data.notifications.find((item) => item.notificationId === notificationId);
    if (!match) return;
    setSelected(match);
    setTab("inbox");
    void companionRequest(user, `/notifications/${notificationId}/device-ack`, { method: "POST", body: "{}" }).catch(() => {});
  }, [data, user]);

  async function load() {
    if (!user) return;
    setBusy("load");
    setMessage("");
    try {
      const [bootstrap, roomResult] = await Promise.all([
        companionRequest<CompanionBootstrap>(user, "/bootstrap"),
        decisionRoomRequest<OpportunityDecisionRoom>(user).then((room) => ({ room, error: "" })).catch((error) => ({ room: null, error: error instanceof Error ? error.message : "DECISION_ROOM_FAILED" })),
      ]);
      setData(bootstrap);
      setDecisionRoom(roomResult.room);
      setDecisionError(roomResult.error);
    } catch (error) {
      setMessage(apiMessage(error, ar));
    } finally { setBusy(null); }
  }

  async function simulate() {
    if (!user) return;
    setBusy("simulate");
    setMessage("");
    try {
      const result = await companionRequest<{ notification: CompanionNotification }>(user, "/simulate", { method: "POST", body: "{}" });
      await load();
      setSelected(result.notification);
      setTab("inbox");
      setMessage(t("هيرمس بدأ رسالة جديدة من الخادم بنجاح.", "Hermes initiated a new server-side message."));
    } catch (error) {
      setMessage(apiMessage(error, ar));
    } finally { setBusy(null); }
  }

  async function enablePush() {
    if (!user || !data) return;
    setBusy("push");
    setMessage("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("PUSH_NOT_SUPPORTED");
      let publicKey = data.push.publicKey;
      if (!data.push.configured || !publicKey) {
        const bootstrapped = await bootstrapOwnerPush(user);
        publicKey = String(bootstrapped.integration.config?.vapidPublic ?? "");
        if (!publicKey) throw new Error("WEBPUSH_KEYS_REQUIRED");
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("PUSH_PERMISSION_DENIED");
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      const subscription = current ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: pushKeyBytes(publicKey) as BufferSource,
      });
      await companionRequest(user, "/subscriptions", { method: "POST", body: JSON.stringify(subscription.toJSON()) });
      await load();
      setMessage(t("تم ربط هذا الجهاز بتنبيهات هيرمس.", "This device is now connected to Hermes alerts."));
    } catch (error) {
      setMessage(apiMessage(error, ar));
    } finally { setBusy(null); }
  }

  async function openNotification(notification: CompanionNotification) {
    setSelected(notification);
    if (!user || notification.opened) return;
    try {
      await companionRequest(user, `/notifications/${notification.notificationId}/open`, { method: "POST", body: "{}" });
      setData((current) => current ? {
        ...current,
        unreadCount: notification.acknowledged ? current.unreadCount : Math.max(0, current.unreadCount - 1),
        notifications: current.notifications.map((item) => item.notificationId === notification.notificationId ? { ...item, opened: true } : item),
      } : current);
    } catch { /* the immutable message remains visible even if the receipt is retried later */ }
  }

  async function acknowledge(notification: CompanionNotification) {
    if (!user) return;
    setBusy("ack");
    try {
      await companionRequest(user, `/notifications/${notification.notificationId}/acknowledge`, { method: "POST", body: "{}" });
      await load();
      setSelected((current) => current ? { ...current, acknowledged: true, latestState: "ACKNOWLEDGED" } : current);
    } catch (error) {
      setMessage(apiMessage(error, ar));
    } finally { setBusy(null); }
  }

  async function sendChat() {
    if (!user || busy === "chat" || chatInput.trim().length < 2) return;
    const ownerMessage: HermesChatMessage = { role: "user", content: chatInput.trim() };
    const history = chatMessages.slice(-10);
    setChatMessages((current) => [...current, ownerMessage]);
    setChatInput("");
    setBusy("chat");
    setMessage("");
    try {
      const result = await hermesChatRequest(user, {
        sessionId: `companion-${user.uid}`,
        locale: ar ? "ar" : "en",
        message: ownerMessage.content,
        history,
        decisionRoom,
      });
      setChatMessages((current) => [...current, { role: "assistant", content: result.content }]);
    } catch (error) {
      setChatMessages((current) => [...current, { role: "assistant", content: apiMessage(error, ar) }]);
    } finally { setBusy(null); }
  }

  function chooseTab(next: TabId) {
    setTab(next);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    window.history.replaceState({}, "", url);
  }

  async function installCompanion() {
    if (installed) {
      setMessage(t("Companion مثبت بالفعل على الجهاز.", "Companion is already installed on this device."));
      return;
    }
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setInstallPrompt(null);
      return;
    }
    setMessage(t("على iPhone: افتح زر المشاركة في Safari ثم اختر «إضافة إلى الشاشة الرئيسية». بعد التثبيت افتح التطبيق من أيقونة Tradvera لتفعيل التنبيهات.", "On iPhone: open Safari Share, choose Add to Home Screen, then launch the Tradvera icon to enable notifications."));
  }

  return <div className="companion-shell" dir={ar ? "rtl" : "ltr"}>
    <header className="companion-head">
      <a className="companion-brand" href="/" aria-label="Tradvera">
        <img src="/brand/tradvera-mark.svg" alt="" />
        <span><b>TRADVERA</b><small>Learn. Decide. Trade</small></span>
      </a>
      <div className="companion-head-actions">
        <button onClick={() => void installCompanion()} aria-label={t("تثبيت التطبيق", "Install app")}><Download size={18}/><span>{installed ? t("مثبت", "Installed") : t("تثبيت", "Install")}</span></button>
        <button onClick={() => setLocale(ar ? "en" : "ar")} aria-label={t("English", "العربية")}><Languages size={18}/><span>{ar ? "EN" : "ع"}</span></button>
        <AccountControl />
      </div>
    </header>

    <main className="companion-stage">
      <section className="companion-status-line">
        <span className="companion-live"><i/>{t("هيرمس حاضر", "Hermes online")}</span>
        <span>{t("مساعدك التنفيذي للتداول", "Your trading executive assistant")}</span>
        <button onClick={() => void load()} disabled={!user || busy === "load"} aria-label={t("تحديث", "Refresh")}><RefreshCw size={15} className={busy === "load" ? "is-spinning" : ""}/></button>
      </section>

      {message && <div className="companion-message" role="status"><Radio size={15}/><span>{message}</span></div>}
      {preview && <div className="companion-preview-note"><TriangleAlert size={16}/>{t("المعاينة المحلية تعرض الهيكل فقط. سجّل بحساب المالك لاختبار المبادرة والتخزين.", "Local preview shows the shell only. Sign in as owner to test initiative and storage.")}</div>}

      {tab === "chat" && <HermesHome ar={ar} data={data} newest={newest} busy={busy} chatInput={chatInput} chatMessages={chatMessages} onChatInput={setChatInput} onSendChat={() => void sendChat()} onSimulate={() => void simulate()} onEnablePush={() => void enablePush()} onOpen={(item) => void openNotification(item)} />}
      {tab === "inbox" && <NotificationInbox ar={ar} notifications={data?.notifications ?? []} busy={busy} onOpen={(item) => void openNotification(item)} />}
      {tab === "portfolio" && <PortfolioPocket ar={ar} portfolio={data?.portfolio ?? null} financialReport={data?.financialReport ?? null} busy={busy === "load"} />}
      {tab === "decisions" && <MobileDecisionRoom ar={ar} user={user} room={decisionRoom} error={decisionError} busy={busy === "load"} onRoom={setDecisionRoom} onReload={() => void load()} />}
      {tab === "actions" && <MobileSafeActions ar={ar} onSimulate={() => void simulate()} busy={busy} />}
    </main>

    <nav className="companion-nav" aria-label={t("تنقل Companion", "Companion navigation")}>
      {tabs.map((item) => {
        const Icon = item.icon;
        return <button key={item.id} className={tab === item.id ? "is-active" : ""} onClick={() => chooseTab(item.id)}>
          <span><Icon size={20}/>{item.id === "inbox" && unreadCount > 0 && <i>{unreadCount > 9 ? "9+" : unreadCount}</i>}</span>
          <small>{ar ? item.ar : item.en}</small>
        </button>;
      })}
    </nav>

    {selected && <NotificationSheet ar={ar} notification={selected} busy={busy === "ack"} onClose={() => setSelected(null)} onAcknowledge={() => void acknowledge(selected)} />}
  </div>;
}

function HermesHome({ ar, data, newest, busy, chatInput, chatMessages, onChatInput, onSendChat, onSimulate, onEnablePush, onOpen }: {
  ar: boolean;
  data: CompanionBootstrap | null;
  newest: CompanionNotification | null;
  busy: string | null;
  chatInput: string;
  chatMessages: HermesChatMessage[];
  onChatInput: (value: string) => void;
  onSendChat: () => void;
  onSimulate: () => void;
  onEnablePush: () => void;
  onOpen: (item: CompanionNotification) => void;
}) {
  const t = (arabic: string, english: string) => ar ? arabic : english;
  return <div className="companion-home">
    <section className="hermes-chat-card">
      <header><span><Bot size={22}/><b>{t("هيرمس المدير التنفيذي", "Executive Hermes")}</b></span><small>{data?.financialReport ? `WALLET LINK · ${data.financialReport.books.length} BOOK${data.financialReport.books.length === 1 ? "" : "S"}` : "WALLET LINK · LOADING"}</small></header>
      <div className="hermes-chat-log" aria-live="polite">
        <article className="is-hermes"><span>EXECUTIVE HERMES</span><p>{t("أنا نقطة أوامرك الواحدة يا وائل. محفظتك مربوطة بيا من المصدر الآمن؛ اطلب «تقريري المالي الحقيقي» أو «رصيدي الافتراضي» وهعرض الأرقام الموثقة من غير خلط ولا أصفار وهمية. وتقدر كمان تقول: «قيّم شركة NVDA»، «حدّث الفرص»، أو «اعرض غرفة القرار».", "I am your single command point. Your wallet is linked through the secure source; ask for ‘my real financial report’ or ‘my virtual balance’ and I will show verified figures without mixing books or fabricating zeros. You can also say: ‘Evaluate NVDA’, ‘Refresh opportunities’, or ‘Show Decision Room’.")}</p></article>
        {chatMessages.map((item, index) => <article key={`${item.role}-${index}`} className={item.role === "user" ? "is-owner" : "is-hermes"}><span>{item.role === "user" ? "WAEL" : "HERMES"}</span><p>{item.content}</p></article>)}
        {busy === "chat" && <article className="is-hermes is-thinking"><span>HERMES</span><p><LoaderCircle className="is-spinning" size={16}/>{t("براجع السياق الموثق…", "Reviewing verified context…")}</p></article>}
      </div>
      <form onSubmit={(event) => { event.preventDefault(); onSendChat(); }}>
        <input value={chatInput} onChange={(event) => onChatInput(event.target.value)} disabled={busy === "chat"} placeholder={t("مثال: اعرض تقريري المالي", "Example: Show my financial report")} aria-label={t("رسالتك لهيرمس", "Your message to Hermes")}/>
        <button type="submit" disabled={busy === "chat" || chatInput.trim().length < 2} aria-label={t("إرسال", "Send")}><SendHorizontal size={20}/></button>
      </form>
      <footer><ShieldCheck size={14}/><span>{t("أوامر القرار تُسجل فقط؛ لا يُرسل هيرمس أمرًا إلى البروكر.", "Decision commands are recorded only; Hermes never sends a broker order.")}</span></footer>
    </section>

    <section className="companion-action-stack">
      <div className="push-card">
        <div><Smartphone size={20}/><span><b>{t("تنبيهات الشاشة المقفولة", "Lock-screen notifications")}</b><small>{data?.push.configured ? t(`${data.push.activeSubscriptions} جهاز مرتبط`, `${data.push.activeSubscriptions} connected device(s)`) : t("ينتظر مفاتيح Web Push", "Web Push keys pending")}</small></span></div>
        <button disabled={!data || busy === "push"} onClick={onEnablePush}>{busy === "push" ? <LoaderCircle className="is-spinning" size={16}/> : <BellRing size={16}/>} {t(data?.push.configured ? "فعّل" : "هيّئ وفعّل", data?.push.configured ? "Enable" : "Set up & enable")}</button>
      </div>
      <button className="hermes-test-button" onClick={onSimulate} disabled={busy === "simulate"}>
        <span>{busy === "simulate" ? <LoaderCircle className="is-spinning"/> : <Sparkles/>}</span>
        <div><b>{t("اختبر مبادرة هيرمس الآن", "Test Hermes initiative now")}</b><small>{t("الخادم يبدأ رسالة دون سؤال سابق", "Server initiates a message without a prior prompt")}</small></div>
        <ChevronLeft size={18}/>
      </button>
      {newest ? <button className={`latest-hermes severity-${newest.severity}`} onClick={() => onOpen(newest)}>
        <span className="latest-code">{newest.severity.toUpperCase()}</span>
        <div><small>{new Date(newest.createdAt).toLocaleString(ar ? "ar-EG" : "en-GB")}</small><h3>{ar ? newest.titleAr : newest.titleEn}</h3><p>{ar ? newest.bodyAr : newest.bodyEn}</p></div>
        <ChevronLeft size={18}/>
      </button> : <div className="companion-empty compact"><Radio size={24}/><b>{t("الوارد هادئ", "Inbox is quiet")}</b><span>{t("أول رسالة حقيقية ستظهر هنا.", "The first real message will appear here.")}</span></div>}
    </section>
  </div>;
}

function NotificationInbox({ ar, notifications, busy, onOpen }: { ar: boolean; notifications: CompanionNotification[]; busy: string | null; onOpen: (item: CompanionNotification) => void }) {
  const t = (arabic: string, english: string) => ar ? arabic : english;
  return <section className="inbox-view">
    <div className="companion-section-head"><div><small>HERMES INBOX</small><h1>{t("سجل لا يُكتب فوقه", "An append-only inbox")}</h1></div><span>{notifications.length.toString().padStart(2, "0")}</span></div>
    <p className="section-lead">{t("كل تنبيه يحتفظ بمصدره وسبب أولويته وحالات تسليمه. القراءة لا تمسح الرسالة، والاعتماد يضاف كحدث جديد.", "Each alert preserves its source, priority rationale, and delivery history. Reading never erases it; acknowledgement adds a new event.")}</p>
    {busy === "load" && !notifications.length ? <div className="companion-empty"><LoaderCircle className="is-spinning"/><b>{t("نستعيد السجل…", "Restoring the ledger…")}</b></div> : notifications.length ? <div className="notification-ledger">{notifications.map((item) => <button key={item.notificationId} className={`notification-row severity-${item.severity}${item.opened ? " is-opened" : ""}`} onClick={() => onOpen(item)}>
      <i/>
      <span className="notification-time">{new Date(item.createdAt).toLocaleTimeString(ar ? "ar-EG" : "en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
      <div><small>{item.sourceLabel} · {item.latestState}</small><h3>{ar ? item.titleAr : item.titleEn}</h3><p>{ar ? item.bodyAr : item.bodyEn}</p></div>
      {item.acknowledged ? <Check size={17}/> : <ChevronLeft size={17}/>}
    </button>)}</div> : <div className="companion-empty"><Inbox size={28}/><b>{t("لا توجد رسائل بعد", "No messages yet")}</b><span>{t("استخدم اختبار المبادرة لإثبات المسار الكامل.", "Use the initiative test to prove the full path.")}</span></div>}
  </section>;
}

function PortfolioPocket({ ar, portfolio, financialReport, busy }: { ar: boolean; portfolio: CompanionPortfolio | null; financialReport: HermesFinancialReportV1 | null; busy: boolean }) {
  if (financialReport) return <FinancialReportPocket ar={ar} report={financialReport} busy={busy}/>;
  return <LegacyPortfolioPocket ar={ar} portfolio={portfolio} busy={busy}/>;
}

function LegacyPortfolioPocket({ ar, portfolio, busy }: { ar: boolean; portfolio: CompanionPortfolio | null; busy: boolean }) {
  const t = (arabic: string, english: string) => ar ? arabic : english;
  const money = (value: number | null) => typeof value === "number"
    ? new Intl.NumberFormat(ar ? "ar-EG" : "en-US", { style: "currency", currency: portfolio?.currency ?? "USD", maximumFractionDigits: 2 }).format(value)
    : "—";
  const connected = portfolio?.sourceState === "connected";
  return <section className="portfolio-pocket">
    <div className="companion-section-head"><div><small>PORTFOLIO POCKET</small><h1>{t("وضعك في جيبك", "Your position, in your pocket")}</h1></div><WalletCards/></div>
    <p className="section-lead">{t("هذه الصفحة تقرأ آخر كشف Paper موثق وتفصله تمامًا عن الأموال الحقيقية وWatchlist. هيرمس لا يملأ أي فجوة برقم افتراضي.", "This page reads the latest verified Paper statement and keeps it isolated from real capital and Watchlist. Hermes never fills a missing fact with a synthetic number.")}</p>
    <div className="portfolio-mode-card">
      <span className={connected ? "is-connected" : "is-unbound"}>{busy ? "SYNCING" : connected ? "PAPER SOURCE · CONNECTED" : "PAPER SOURCE · UNBOUND"}</span>
      <div className="portfolio-pocket-balance">
        <small>{t("القيمة الحالية في المحاكاة", "Current simulated equity")}</small>
        <strong>{busy ? "…" : money(portfolio?.currentEquity ?? null)}</strong>
        <em>PAPER · VIRTUAL · REAL MONEY: NO</em>
      </div>
      <div className="portfolio-pocket-kpis">
        <div><small>{t("ربح محقق", "Realized P/L")}</small><b>{money(portfolio?.realizedPnl ?? null)}</b></div>
        <div><small>{t("ربح غير محقق", "Unrealized P/L")}</small><b>{money(portfolio?.unrealizedPnl ?? null)}</b></div>
        <div><small>{t("المراكز / الأوامر", "Positions / orders")}</small><b>{portfolio ? `${portfolio.positionsCount} / ${portfolio.currentOrdersCount}` : "—"}</b></div>
      </div>
      <div className="portfolio-pocket-truth">
        <ShieldCheck size={16}/><span><b>{connected ? t("المصدر موثوق ومربوط", "Verified source connected") : t("في انتظار أول كشف موثق", "Waiting for the first verified statement")}</b><small>{portfolio?.asOf ? new Date(portfolio.asOf).toLocaleString(ar ? "ar-EG" : "en-GB") : t("لن تُعرض أصفار وهمية", "No fabricated zeros will be shown")}</small></span>
      </div>
      <h2>{t("ثلاثة صناديق، ثلاث نوافذ زمنية", "Three sleeves, three time horizons")}</h2>
      <div className="sleeve-list">
        {(portfolio?.sleeves.length ? portfolio.sleeves : [
          { id: "intraday_speculator", labelAr: "صندوق المضارب", labelEn: "Speculator sleeve", enabled: false, capitalPct: 0, capitalAmount: null, cadence: "real_time", horizons: ["intraday"] },
          { id: "swing_trader", labelAr: "صندوق المتداول", labelEn: "Trader sleeve", enabled: false, capitalPct: 0, capitalAmount: null, cadence: "eod", horizons: ["weekly", "monthly"] },
          { id: "long_term_investor", labelAr: "صندوق المستثمر", labelEn: "Investor sleeve", enabled: false, capitalPct: 0, capitalAmount: null, cadence: "weekly", horizons: ["position"] },
        ] satisfies CompanionPortfolio["sleeves"]).map((sleeve) => <div key={sleeve.id} className={sleeve.enabled ? "is-enabled" : "is-disabled"}>
          <span><b>{ar ? sleeve.labelAr : sleeve.labelEn}</b><small>{sleeve.horizons.join(" → ").toUpperCase()} · {sleeve.cadence.toUpperCase()}</small></span>
          <strong>{sleeve.enabled ? `${sleeve.capitalPct}%` : t("متوقف", "Off")}</strong>
          <em>{sleeve.enabled ? money(sleeve.capitalAmount) : "—"}</em>
        </div>)}
      </div>
      <div className="portfolio-pocket-policy"><span>POLICY</span><b>{portfolio?.policyState?.toUpperCase() ?? "UNBOUND"}</b><small>{portfolio?.planVersion === null || portfolio?.planVersion === undefined ? "—" : `V${portfolio.planVersion}`}</small></div>
      <a href="/?world=trade"><ExternalLink size={16}/>{t("افتح منصة التداول الكاملة", "Open full Trade platform")}</a>
    </div>
  </section>;
}

function NotificationSheet({ ar, notification, busy, onClose, onAcknowledge }: { ar: boolean; notification: CompanionNotification; busy: boolean; onClose: () => void; onAcknowledge: () => void }) {
  const t = (arabic: string, english: string) => ar ? arabic : english;
  return <div className="notification-sheet-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <article className={`notification-sheet severity-${notification.severity}`} role="dialog" aria-modal="true" aria-labelledby="notification-title">
      <div className="sheet-grip"/>
      <header><span>{notification.severity.toUpperCase()} · {notification.latestState}</span><button onClick={onClose}>×</button></header>
      <small>{notification.sourceLabel} · {new Date(notification.knownAt).toLocaleString(ar ? "ar-EG" : "en-GB")}</small>
      <h2 id="notification-title">{ar ? notification.titleAr : notification.titleEn}</h2>
      <p>{ar ? notification.bodyAr : notification.bodyEn}</p>
      <div className="sheet-evidence"><span>{t("الدليل المسجل", "Recorded evidence")}</span><p>{ar ? notification.evidence.evidenceAr : notification.evidence.evidenceEn}</p></div>
      <div className="sheet-facts">
        <div><small>MATERIALITY</small><b>{notification.evidence.materialityScore ?? "—"}</b></div>
        <div><small>WINDOW</small><b>{notification.evidence.timeSensitivityMinutes ?? "—"}m</b></div>
        <div><small>SOURCE</small><b>{notification.sourceHealth}</b></div>
      </div>
      <div className="sheet-actions">
        <a href={notification.deepLink}><ExternalLink size={16}/>{t("افتح السياق", "Open context")}</a>
        <button onClick={onAcknowledge} disabled={busy || notification.acknowledged}>{notification.acknowledged ? <Check size={16}/> : busy ? <LoaderCircle className="is-spinning" size={16}/> : <ShieldCheck size={16}/>} {notification.acknowledged ? t("تمت المراجعة", "Reviewed") : t("سجل المراجعة", "Acknowledge")}</button>
      </div>
    </article>
  </div>;
}

function apiMessage(error: unknown, ar: boolean) {
  const code = error instanceof Error ? error.message : "COMPANION_FAILED";
  const messages: Record<string, [string, string]> = {
    PUSH_NOT_SUPPORTED: ["هذا المتصفح لا يدعم Web Push.", "This browser does not support Web Push."],
    PUSH_PERMISSION_DENIED: ["إذن التنبيهات مرفوض على هذا الجهاز.", "Notification permission was denied on this device."],
    NOTIFICATION_CANDIDATE_REJECTED: ["محرك السياسة رفض الحدث؛ لم تُنشأ رسالة مضللة.", "The policy engine rejected the event; no misleading alert was created."],
    FAILED_TO_FETCH: ["تعذر الوصول إلى Notification Core الآن.", "Notification Core is currently unreachable."],
  };
  const value = messages[code] ?? ["تعذر إكمال العملية الآن؛ لم نفقد السجل.", "The operation could not complete; the ledger is intact."];
  return ar ? value[0] : value[1];
}
